
import Vue from 'vue';
import RunnitSingleTool from './RunnitSingleTool.vue';
import RunnitWorkflow from './RunnitWorkflow.vue';
import RunnitTemplate from './RunnitTemplate.vue';
import RunnitTraining from './RunnitTraining.vue';
import { ROUTER } from '@/router/constants';
import { mapActions, mapState } from 'vuex';
import { Runnit, RunnitNode, RUNNIT_TYPE } from '@run-diffusion/shared';
import { db } from '@/firebase';
import { get$bindFirestoreOptions, TeamUserMixin } from '@/mixins';
import { SNACKBAR_STATUS } from '@/constants/constants';
import AnnouncementsBanner from '@/components/Toolbar/AnnouncementsBanner.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import RunnitBatchGenerationDrawer from '@/views/Runnits/RunnitBatchProcessing/RunnitBatchGenerationDrawer.vue';
import { isNodeBatchEligible } from '@/utils/runnitBatchVariableFields';
import RunnitBoardSkeleton from '@/views/Runnits/RunnitBoardSkeleton.vue';
import FeatureTourShell from '@/featureTour/components/FeatureTourShell.vue';
import { featureTourService } from '@/featureTour/services/featureTourService';
import { isFeatureTourEligibleViewport } from '@/featureTour/utils/viewport';
import { boardFirstOpen, singleToolFirstOpen } from '@/featureTour/tours';

export default Vue.extend({
	name: 'RunnitBoard',
	mixins: [
		TeamUserMixin,
	],
	metaInfo () {
		return {
			title: this.currentRunnit ? this.currentRunnit.title : 'Loading...',
		};
	},
	data () {
		return {
			RUNNIT_TYPE,
			loadingCurrentRunnit: false,
			currentRunnit: null as Runnit | null,
			batchWizardOpen: false,
			// One-shot latch: the runnitNodes watcher below fires on every
			// rebind (board switch, realtime update). Without this guard, a
			// rebind mid-tour would call back into featureTourService.start()
			// — which calls stop() on any active tour — and yank the user out
			// at whichever step they're on. The persistence "hasSeenTour" gate
			// inside maybeAutoStart already prevents this in practice, but
			// pinning the intent at the call site means we don't rely on
			// internal service behavior staying that way.
			boardTourLaunchAttempted: false,
			singleToolTourLaunchAttempted: false,
		};
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'updateRunnitState',
			'bindRunnitNodes',
			'unbindRunnitNodes',
			'setLoadingRunnitNodes',
			'setPendingRunnit',
		]),
		async confirmCloseBatchDrawer (): Promise<boolean> {
			if (!this.batchWizardOpen) return true;
			const drawer: any = this.$refs.batchDrawer;
			if (!drawer) return true;
			return drawer.requestClose();
		},
		async maybeLaunchBoardTour () {
			// Already attempted once for this component instance — don't re-fire
			// when runnitNodes rebinds (board switch, realtime update). See the
			// boardTourLaunchAttempted comment in data() for the full reasoning.
			if (this.boardTourLaunchAttempted) return;

			if (!isFeatureTourEligibleViewport(this.$vuetify)) return;

			// Don't auto-launch if we're loading, in a single tool, or training.
			// The tour is for boards/workflows only. Templates DO get the tour —
			// step 6 swaps from Run to Create Board for them, and add-tool gates
			// itself out via canShow.
			if (this.loadingCurrentRunnit || this.isSingleTool || this.isTraining) return;

			// Final guard: only launch if the board is actually ready. Empty boards
			// (no nodes yet) are fine — the tool-dependent steps gate themselves
			// via canShow on each step.
			if (!this.currentRunnit || !this.currentRunnit.id) return;

			// Latch BEFORE the await so a fast-arriving second watcher tick
			// during the same microtask can't slip through and call start() twice.
			this.boardTourLaunchAttempted = true;
			await featureTourService.maybeAutoStart(boardFirstOpen);
		},
		async maybeLaunchSingleToolTour () {
			// Same one-shot latch reasoning as maybeLaunchBoardTour — guards
			// against re-firing on runnitNodes rebinds.
			if (this.singleToolTourLaunchAttempted) return;

			if (!isFeatureTourEligibleViewport(this.$vuetify)) return;

			// Single-tool tour is the parallel of boardFirstOpen for users
			// landing on a SINGLE_TOOL board. Skip workflow/template/training.
			if (this.loadingCurrentRunnit || !this.isSingleTool || this.isTraining) return;

			if (!this.currentRunnit || !this.currentRunnit.id) return;

			// The tour itself gates on prompt-driven mode being active via its
			// autoLaunch callback — no need to duplicate that check here.
			this.singleToolTourLaunchAttempted = true;
			await featureTourService.maybeAutoStart(singleToolFirstOpen);
		},
		async localBindRunnit (runnitId: string) {
			try {
				this.loadingCurrentRunnit = true;
				// Mark nodes as loading up-front so the skeleton stays visible
				// continuously through doc-bind → nodes-bind (no flicker between).
				this.setLoadingRunnitNodes(true);
				await this.$bind(
					'currentRunnit',
					db.doc(`runnits/${runnitId}`),
					get$bindFirestoreOptions(),
				);
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error loading ${this.runnitType === RUNNIT_TYPE.SINGLE_TOOL ? 'Tool' : this.runnitType === RUNNIT_TYPE.TEMPLATE ? 'Template' : 'Board'}`,
					show: true,
				});
			} finally {
				this.loadingCurrentRunnit = false;
				// Bound doc is the authority once it's here — drop the pending
				// hint so a stale type can't outlive the load it was set for.
				this.setPendingRunnit(null);
			}
		},
		openBatchWizard () {
			if (!this.canOpenBatchWizard) return;
			this.batchWizardOpen = true;
		},
	},
	destroyed () {
		this.unbindRunnitNodes();
		this.setPendingRunnit(null);
		this.updateRunnitState({
			runnitDraft: null,
			runnitDraftNodeRef: null,
			runnit: null,
			selectedNodeId: null,
			promptGeneratorOpen: false,
			imageSelectOpen: false,
			inpaintingEditorOpen: false,
			inpaintingEditorSaving: false,
			inpaintingBackgroundImageUrl: null,
			inpaintingMaskUrl: null,
			inputValues: {},
			staticInputValues: {},
			settingsDrawerOpen: false,
		});
	},
	computed: {
		...mapState([
			'runnitState',
			'runnitNodes',
			'runnitNodesMap',
			'loadingRunnitNodes',
			'pendingRunnit',
			'user',
		]),
		boardInitializing (): boolean {
			// One unified loading state covers both the runnit doc bind and the
			// initial runnitNodes bind. loadingRunnitNodes starts as null and is
			// set to false only after the nodes bind resolves, so we treat any
			// non-false value as still-loading.
			return this.loadingCurrentRunnit || this.loadingRunnitNodes !== false;
		},
		runnitType (): string | null {
			// pendingRunnit is set in Vuex by the click handler that pushed the
			// new route, before $bind starts. Paired with id so a stale value
			// from a prior nav can't bleed into the current load window.
			if (this.pendingRunnit && this.pendingRunnit.id === this.$route.params.runnitId) {
				return this.pendingRunnit.type;
			}
			return this.currentRunnit?.type || null;
		},
		selectedNode (): RunnitNode | null {
			const id = this.runnitState?.selectedNodeId;
			return id ? this.runnitNodesMap[id] || null : null;
		},
		isSingleTool () {
			return this.runnitState?.runnit?.type === RUNNIT_TYPE.SINGLE_TOOL;
		},
		isTraining () {
			return this.runnitNodes?.some(
				node => node?.nodeDef?.appType === 'TRAINER'
			);
		},
		isTemplateView () {
			return this.runnitState?.runnit?.type === RUNNIT_TYPE.TEMPLATE;
		},
		canOpenBatchWizard (): boolean {
			return isNodeBatchEligible(this.selectedNode) && this.hasBatchGenerationPermission;
		},
	},
	watch: {
		'$route.params.runnitId': {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal !== oldVal) {
					this.localBindRunnit(newVal);
				}
			},
		},
		currentRunnit: {
			immediate: true,
			handler (newVal: Runnit, oldVal: Runnit) {
				if (newVal?.deletedAt) {
					// Navigate away if the runnit has been deleted
					this.routerReplace(this.$route, this.$router, { name: ROUTER.RUNNIT_BOARDS_PAGE });
					return;
				}
				// A canvas board (Boards v2) has no runnitNodes and opens in the canvas
				// app at /board/:id; send it there before anything binds for v1.
				if (newVal?.type === RUNNIT_TYPE.CANVAS && newVal.id) {
					this.routerReplace(this.$route, this.$router, { name: ROUTER.BOARD, params: { boardId: newVal.id } });
					return;
				}

				if (newVal !== oldVal) {
					const newRunnitId: string = this._get(newVal, 'id') || null;
					const oldRunnitId: string = this._get(this.runnitState.runnit, 'id') || null;
					this.updateRunnitState({
						runnit: newVal,
						...(newRunnitId !== oldRunnitId && {
							selectedNodeId: null,
						}),
					});

					// Bind runnitNodes when currentRunnit changes
					if (newVal && newVal.id) {
						this.bindRunnitNodes({ runnitId: newVal.id });
					} else {
						this.unbindRunnitNodes();
					}
				}
			},
		},
		canOpenBatchWizard (canOpen: boolean) {
			if (!canOpen) {
				this.batchWizardOpen = false;
			}
		},
		runnitNodes: {
			immediate: true,
			handler (newVal) {
				// Fire whenever the bind resolves (including an empty array for
				// brand-new boards). The tour itself filters tool-dependent steps
				// via canShow, so an empty board still gets a meaningful tour.
				if (Array.isArray(newVal)) {
					this.maybeLaunchBoardTour();
					this.maybeLaunchSingleToolTour();
				}
			},
		},
	},
	components: {
		AnnouncementsBanner,
		LoadingState,
		RunnitBatchGenerationDrawer,
		RunnitBoardSkeleton,
		RunnitSingleTool,
		RunnitWorkflow,
		RunnitTemplate,
		RunnitTraining,
		FeatureTourShell,
	},
});
